/**
 * Credit Warning Banner
 *
 * Shows warning when paid tier credits drop below 10%.
 * Hidden entirely for free tier (they see message count in sidebar instead).
 * Displays once per session (sessionStorage).
 */

import { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, X, Zap } from 'lucide-react';
import { colors } from '@/theme';
import { creditsAPI, type CreditBalance } from '@/services/api';
import { wsService } from '@/services/websocket';
import { useAuthStore } from '@/stores/auth';
import { useSubscriptionStore } from '@/stores/subscription';

export function CreditWarningBanner() {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const { user } = useAuthStore();
  const { isFreeTier } = useSubscriptionStore();

  const fetchBalance = useCallback(async () => {
    try {
      const data = await creditsAPI.getBalance();
      setBalance(data);
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchBalance();

    // Refresh when tasks complete
    const unsubscribe = wsService.subscribeTasks(() => {
      fetchBalance();
    });

    return () => unsubscribe();
  }, [fetchBalance]);

  // Free tier uses message count in sidebar — no banner needed
  if (isFreeTier()) return null;

  if (!balance || dismissed) return null;

  const { displayAvailable, monthlyCredits } = balance;
  const maxCredits = Math.max(monthlyCredits, 1);
  const pct = (displayAvailable / maxCredits) * 100;

  // Only show when less than 10% remaining
  const sessionKey = `credit-warning-dismissed-${user?.id}`;
  if (pct >= 10 || sessionStorage.getItem(sessionKey)) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(sessionKey, 'true');
    setDismissed(true);
  };

  const warningColor = pct < 5 ? '#EF4444' : colors.moltenGold;
  const warningText = pct < 5
    ? 'Critical: Less than 5% credits remaining'
    : 'Credits running low: Less than 10% remaining';

  return (
    <div
      className="flex items-center justify-between gap-4 px-4 py-3 mb-4 rounded-lg border"
      style={{
        background: `${warningColor}15`,
        borderColor: warningColor,
      }}
    >
      <div className="flex items-center gap-3">
        <AlertTriangle size={20} style={{ color: warningColor }} />
        <div className="flex-1">
          <p className="text-sm font-medium" style={{ color: colors.sandstone }}>
            {warningText}
          </p>
          <p className="text-xs mt-1" style={{ color: colors.secondaryText }}>
            {displayAvailable.toLocaleString()} of {monthlyCredits.toLocaleString()} credits remaining ({Math.round(pct)}%) — Top up to avoid interruptions
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href="/credits"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
          style={{
            background: colors.foundryCopper,
            color: colors.obsidian,
          }}
        >
          <Zap size={16} />
          Top Up
        </a>
        <button
          onClick={handleDismiss}
          className="p-1 rounded hover:bg-white/10 transition-colors"
          style={{ color: colors.secondaryText }}
          aria-label="Dismiss warning"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
